// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
} from "../core/schema.js";
import {
  metricHasScoredFinding,
} from "../engine/review-model.js";
import {
  reportTurboContextNote,
  reportTurboContextReview,
  reportTurboContextStatus,
  reportTurboLoggedChannels,
  reportTurboTitQualityCue,
} from "./pdf-helpers.js";
import {
  flightRouteHasEndpoints,
  flightRouteSummaryText,
} from "../app/flight-route-display.js";
import {
  pdfColorForScore,
  pdfPalette,
} from "./pdf-document.js";

function defaultPanelNumber(value, suffix = "", digits = 0) {
  return Number.isFinite(value) ? `${Number(value).toFixed(digits)}${suffix}` : "n/a";
}

function defaultSignedPanelNumber(value, suffix = "", digits = 0) {
  if (!Number.isFinite(value)) return "n/a";
  const abs = Math.abs(value).toFixed(digits);
  return `${value > 0 ? "+" : value < 0 ? "-" : ""}${abs}${suffix}`;
}

function defaultQuantile(values, q) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.floor((sorted.length - 1) * q)));
  return sorted[index];
}

function finiteValuesForKey(data, key) {
  return data.map((point) => point[key]).filter(Number.isFinite);
}

function reportModelMaxFiniteForKeys(data, keys, minimum = -Infinity) {
  let max = -Infinity;
  keys.forEach((key) => {
    data.forEach((point) => {
      const value = point[key];
      if (Number.isFinite(value) && value > minimum) max = Math.max(max, value);
    });
  });
  return max === -Infinity ? null : max;
}

function reportModelHasFiniteForKey(data, key) {
  return data.some((point) => Number.isFinite(point?.[key]));
}

function isRotaxSummary(summary) {
  return summary?.applicability?.engineFamily === "rotax";
}

function reportEngineProfileNote(summary) {
  if (!isRotaxSummary(summary)) return "";
  const cooling = summary?.applicability?.engineCooling;
  const control = summary?.applicability?.engineControl;
  const parts = ["Rotax engine context"];
  if (cooling === "mixed") parts.push("mixed liquid/air cooling");
  else if (cooling === "liquid") parts.push("liquid cooling");
  else if (cooling === "air") parts.push("air cooling");
  if (control === "ecu") parts.push("ECU-managed");
  else if (control === "manual-mixture") parts.push("manual mixture");
  return `${parts.join(" - ")}. Use Rotax/installation limits for RPM, coolant/head temperature, fuel, and boost interpretation.`;
}

function metricIsApplicable(metric) {
  return metric?.details?.status !== "not-applicable";
}

export function reportMetricMentionableInPdf(key, metric) {
  if (key === "mag") return !metricHasScoredFinding("mag", metric);
  // Transient divergence is overview-only operating context (issue #228): it has
  // no score and must never read as a cylinder problem in the PDF report.
  if (key === "divergence") return false;
  return true;
}

function reportRotaxProfileCard(summary) {
  const note = reportEngineProfileNote(summary);
  if (!note) return null;
  return {
    label: "Engine Profile",
    value: "Rotax",
    detail: note,
    color: pdfPalette.blue,
  };
}

function reportPointAverage(point, keys) {
  return averageValues(keys.map((key) => point[key]).filter(Number.isFinite));
}

function reportPointSpread(point, keys) {
  const values = keys.map((key) => point[key]).filter(Number.isFinite);
  const extent = extentFromValues(values);
  return extent ? extent.max - extent.min : null;
}

export function flightReportRowsModel(summary, {
  flightData = [],
  selectedFlight = null,
  sourceMonitor = "Unknown",
  flightDateLabel = () => "n/a",
  formatPointTime = () => "--",
  formatElapsedTime = defaultPanelNumber,
  flightDurationSeconds = () => 0,
  sampleRateSummary = () => "n/a",
  fuelUsedMetric = () => "Not logged",
  preferredPowerEstimateSummary = () => null,
  dataQualitySummary = () => "n/a",
} = {}) {
  const first = flightData[0];
  const last = flightData.at(-1);
  const aircraft = selectedFlight?.aircraft ?? "Sample";
  const monitor = selectedFlight?.monitor ?? sourceMonitor;
  const maxCht = reportModelMaxFiniteForKeys(flightData, [...cylinderKeys("cht"), "cht"], 0);
  const maxCoolant = reportModelMaxFiniteForKeys(flightData, ["coolantTemp"], 0);
  const maxEgt = reportModelMaxFiniteForKeys(flightData, [...cylinderKeys("egt"), "egt"], 0);
  const powerSummary = preferredPowerEstimateSummary(flightData);
  const profileNote = reportEngineProfileNote(summary);
  const thermalRows = isRotaxSummary(summary)
    ? [
        ["Max coolant", maxCoolant === null ? "Not logged" : `${Math.round(maxCoolant)} F`],
        ...(maxCht === null ? [] : [["Max CHT/head temp", `${Math.round(maxCht)} F`]]),
      ]
    : [["Max CHT", maxCht === null ? "Not logged" : `${Math.round(maxCht)} F`]];
  return [
    ["Aircraft", aircraft],
    ["Date", flightDateLabel(first, selectedFlight)],
    ["Time range", first && last ? `${formatPointTime(first)} - ${formatPointTime(last)}` : "n/a"],
    ...(flightRouteHasEndpoints(selectedFlight) ? [["Route", flightRouteSummaryText(selectedFlight)]] : []),
    ["Monitor", monitor],
    ...(profileNote ? [["Engine profile", profileNote]] : []),
    ["Duration", formatElapsedTime(flightDurationSeconds())],
    ["Samples", flightData.length.toLocaleString()],
    ["Sample rate", sampleRateSummary()],
    ...thermalRows,
    ["Max EGT", maxEgt === null ? "Not logged" : `${Math.round(maxEgt)} F`],
    ["Fuel used", fuelUsedMetric()],
    [powerSummary?.hpLabel ?? "Estimated avg HP", Number.isFinite(powerSummary?.avgHp) ? `${Math.round(powerSummary.avgHp)} hp` : "Not logged"],
    [powerSummary?.torqueLabel ?? "Estimated avg torque", Number.isFinite(powerSummary?.avgTorque) ? `${Math.round(powerSummary.avgTorque)} lb-ft` : "Not logged"],
    ["Power basis", powerSummary?.basis ?? "Not logged"],
    ["Overall health", `${summary.overall.score}/100 - ${summary.overall.label}`],
    ["Data quality", dataQualitySummary()],
  ];
}

export function reportTurboContextModel(summary, {
  panelNumber = defaultPanelNumber,
  scoreLabel = () => "Context",
} = {}) {
  const metric = summary?.turbo;
  const details = metric?.details ?? {};
  if (!details.available) return null;
  const limits = details.limits ?? {};
  const loggedChannels = reportTurboLoggedChannels(details);
  const profileDetail = details.limitCount
    ? `${details.limitCount} limit${details.limitCount === 1 ? "" : "s"} configured`
    : details.configured
      ? "Turbo profile configured"
      : details.inferred
        ? "Inferred from logged channels"
        : "Profile status unknown";
  return {
    score: metric.score,
    status: reportTurboContextStatus(metric),
    subtitle: reportTurboTitQualityCue(metric)
      ? "TIT signal quality affects turbo interpretation"
      : loggedChannels.length ? "MAP, TIT, and boost heat context" : "Turbo profile context without logged channels",
    badge: Number.isFinite(metric.score) ? scoreLabel(metric.score) : "Context",
    note: reportTurboContextNote(metric),
    review: reportTurboContextReview(metric),
    tiles: [
      reportTurboTitQualityCue(metric)
        ? {
            label: "TIT Signal",
            value: Number.isFinite(details.titQuality?.score) ? `${Math.round(details.titQuality.score)}/100` : "Attention",
            detail: details.titQuality?.unstableSampleCount
              ? `${details.titQuality.unstableSampleCount.toLocaleString()} unstable samples`
              : details.titQuality?.impossibleJumpCount
                ? `${details.titQuality.impossibleJumpCount.toLocaleString()} jump crossings`
                : "Signal quality problem",
            color: pdfPalette.red,
          }
        : null,
      details.highPowerHeat?.window
        ? {
            label: "Heat Margin",
            value: details.highPowerHeat.status === "fuel-flow-heat-review" ? "Attention" : "Watch",
            detail: [
              Number.isFinite(details.highPowerHeat.window.map) ? `${details.highPowerHeat.window.map.toFixed(1)} inHg` : null,
              Number.isFinite(details.highPowerHeat.window.fuelFlowShortfall) ? `fuel -${details.highPowerHeat.window.fuelFlowShortfall.toFixed(1)} GPH` : null,
              Number.isFinite(details.highPowerHeat.window.peakTit) ? `TIT ${Math.round(details.highPowerHeat.window.peakTit)} F` : null,
            ].filter(Boolean).join(" · ") || "High-power heat context",
            color: details.highPowerHeat.status === "fuel-flow-heat-review" ? pdfPalette.red : pdfPalette.amber,
          }
        : null,
      {
        label: "Peak TIT",
        value: panelNumber(details.peakTit, " F"),
        detail: Number.isFinite(details.p95Tit)
          ? `p95 ${panelNumber(details.p95Tit, " F")}`
          : Number.isFinite(limits.maxTitF)
            ? `limit ${panelNumber(limits.maxTitF, " F")}`
            : "TIT not logged",
        color: pdfPalette.red,
      },
      {
        label: "Peak MAP",
        value: panelNumber(details.peakMap, " inHg", 1),
        detail: Number.isFinite(limits.maxMapInHg) ? `limit ${panelNumber(limits.maxMapInHg, " inHg", 1)}` : "MAP limit not set",
        color: pdfPalette.purple,
      },
      {
        label: "Upper Deck",
        value: panelNumber(details.peakUpperDeck, " psi", 1),
        detail: "Boost/deck pressure",
        color: pdfPalette.amber,
      },
      {
        label: "Profile",
        value: details.limitCount ? `${details.limitCount} set` : "Limits needed",
        detail: profileDetail,
        color: details.limitCount ? pdfPalette.green : pdfPalette.amber,
      },
    ].filter(Boolean).slice(0, 4),
  };
}

export function reportEngineMetricRowsModel(summary, {
  engineMetricRows = () => [],
  detectionWindowsForHealthKey = () => [],
  engineMetricCylinder = () => null,
} = {}) {
  return engineMetricRows(summary).map((row) => ({
    ...row,
    windows: detectionWindowsForHealthKey(row.key, row.metric),
    cylinder: engineMetricCylinder(row.key, row.metric),
  }));
}

export function reportHealthCheckRowsModel(summary, {
  hasCarbTempChannel = () => false,
} = {}) {
  const rows = isRotaxSummary(summary)
    ? [
        ["Sensors", summary.sensors],
        ...(summary.turbo?.details?.available ? [["Turbo/Boost", summary.turbo]] : []),
        ["Oil System", summary.oilSystem],
        ["Coolant Margin", summary.coolantMargin],
        ["Takeoff Enrichment", summary.takeoffEnrichment],
        [summary.egt?.details?.hot?.isReview ? "High EGT" : "EGT Balance", summary.egt],
        ["EGT Stability", summary.valve],
        ["Start Charge", summary.charging],
        ["Electrical", summary.electrical],
      ]
    : [
        ["Sensors", summary.sensors],
        ...(summary.turbo?.details?.available ? [["Turbo/Boost", summary.turbo]] : []),
        ["Oil System", summary.oilSystem],
        ["CHT Heat", summary.cht],
        [summary.egt?.details?.hot?.isReview ? "High EGT" : "EGT Balance", summary.egt],
        ["EGT Rhythm", summary.valve],
        ["CHT/EGT Pairing", summary.combustion],
        ["Abnormal Combustion", summary.abnormalCombustion],
        ["Induction Leak", summary.inductionLeak],
        ["Mag Check", summary.mag],
        ...(hasCarbTempChannel(summary) ? [["Carb Ice", summary.carbIce]] : []),
        ["Cooling", summary.cooling],
        ["Cooling Rate", summary.coolingRate],
        ["Start Charge", summary.charging],
        ["Electrical", summary.electrical],
      ];
  return rows.filter(([label, metric]) => metricIsApplicable(metric)
    && (label !== "Mag Check" || reportMetricMentionableInPdf("mag", metric))).map(([label, metric]) => ({
    label,
    score: metric?.score,
    note: metric?.note ?? "No data",
    metric,
  }));
}

export function reportTraceRowsModel(summary, {
  flightData = [],
  panelNumber = defaultPanelNumber,
  quantile = defaultQuantile,
  fuelUsedMetric = () => "Not logged",
  estimatedHpMetric = () => "power context",
} = {}) {
  const rows = [];
  const rotax = isRotaxSummary(summary);
  const hasCoolant = reportModelHasFiniteForKey(flightData, "coolantTemp");
  const egtCylinderKeys = cylinderKeys("egt").filter((key) => flightData.some((point) => Number.isFinite(point[key])));
  const chtCylinderKeys = cylinderKeys("cht").filter((key) => flightData.some((point) => Number.isFinite(point[key])));
  const egtKeys = egtCylinderKeys.length ? egtCylinderKeys : flightData.some((point) => Number.isFinite(point.egt)) ? ["egt"] : [];
  const chtKeys = chtCylinderKeys.length ? chtCylinderKeys : flightData.some((point) => Number.isFinite(point.cht)) ? ["cht"] : [];
  const includeChtRows = !rotax || !hasCoolant;
  const addRawTrace = (key, label, suffix, digits, color, summaryText) => {
    const values = flightData.map((point) => point[key]);
    if (values.some(Number.isFinite)) rows.push({ label, values, suffix, digits, color, summary: summaryText });
  };

  if (egtKeys.length) {
    const egtAverageValues = flightData.map((point) => reportPointAverage(point, egtKeys));
    const hot = summary.egt?.details?.hot;
    rows.push({
      label: egtCylinderKeys.length ? "EGT avg" : "EGT trace",
      values: egtAverageValues,
      suffix: " F",
      color: pdfPalette.purple,
      summary: hot?.isReview
        ? `${hot.label ?? "EGT"} peak ${panelNumber(hot.max, " F")}`
        : Number.isFinite(summary.egt?.details?.p90)
        ? `larger gap ${panelNumber(summary.egt.details.p90, " F")}`
        : `high value ${panelNumber(quantile(egtAverageValues, 0.9), " F")}`,
    });
  }
  if (egtCylinderKeys.length > 1) {
    rows.push({
      label: "EGT cylinder gap",
      values: flightData.map((point) => reportPointSpread(point, egtCylinderKeys)),
      suffix: " F",
      color: pdfPalette.red,
      summary: `max ${panelNumber(summary.egt?.details?.max, " F")}`,
    });
  }
  if (includeChtRows && chtKeys.length) {
    const chtAverageValues = flightData.map((point) => reportPointAverage(point, chtKeys));
    rows.push({
      label: chtCylinderKeys.length ? "CHT avg" : "CHT trace",
      values: chtAverageValues,
      suffix: " F",
      color: pdfPalette.amber,
      summary: `high ${panelNumber(summary.cht?.details?.p95 ?? quantile(chtAverageValues, 0.95), " F")}`,
    });
  }
  if (includeChtRows && chtCylinderKeys.length > 1) {
    rows.push({
      label: "CHT spread",
      values: flightData.map((point) => reportPointSpread(point, chtCylinderKeys)),
      suffix: " F",
      color: pdfPalette.green,
      summary: `peak ${panelNumber(summary.cht?.details?.max, " F")}`,
    });
  }
  if (rotax) {
    addRawTrace("coolantTemp", "Coolant temp", " F", 0, pdfPalette.teal, summary.coolantMargin?.note ?? "Rotax cooling context");
  }
  addRawTrace("fuelFlow", "Fuel flow", " GPH", 1, pdfPalette.teal, fuelUsedMetric());
  const pressureEngines = summary.fuelPressure?.details?.engines ?? [];
  if (pressureEngines.length > 1) {
    for (const engine of pressureEngines) addRawTrace(engine.channelKey, `${engine.label} fuel pressure`, " psi", 2, pdfPalette.green, "Advisory pressure context");
  } else {
    addRawTrace(pressureEngines[0]?.channelKey ?? "fuelPressure", "Fuel pressure", " psi", 2, pdfPalette.green, summary.fuelPressure?.note ?? "Recorded pressure context");
  }
  addRawTrace("rpm", "RPM", "", 0, pdfPalette.blue, estimatedHpMetric("cruise"));
  addRawTrace("map", "MAP", " inHg", 1, pdfPalette.purple, "power context");
  if (rotax) {
    addRawTrace("boostPressure", "Boost pressure", " psi", 1, pdfPalette.purple, "Rotax boost context");
  }
  addRawTrace("oilPressure", "Oil pressure", " psi", 0, pdfPalette.green, summary.oilSystem?.note ?? "oil-system context");
  addRawTrace("oilTemp", "Oil temp", " F", 0, pdfPalette.amber, summary.oilSystem?.note ?? "oil-system context");
  addRawTrace("tit", "TIT", " F", 0, pdfPalette.red, Number.isFinite(summary.turbo?.details?.peakTit) ? `peak ${panelNumber(summary.turbo.details.peakTit, " F")}` : "turbo heat context");
  addRawTrace("upperDeckPressure", "Upper deck", " psi", 1, pdfPalette.amber, "turbo pressure context");
  addRawTrace("compressorDischargeTemp", "CDT", " F", 0, pdfPalette.red, "compressor heat context");
  addRawTrace("inductionTemp", "Induction temp", " F", 0, pdfPalette.amber, "induction heat context");
  addRawTrace("buss", "Bus volts", " V", 1, pdfPalette.green, summary.electrical?.note ?? "electrical context");
  return rows.slice(0, rotax ? 10 : 8);
}

export function reportTemperatureValuesModel(prefix, {
  flightData = [],
} = {}) {
  const keys = cylinderKeys(prefix).filter((key) => flightData.some((point) => Number.isFinite(point[key])));
  if (keys.length) return flightData.map((point) => reportPointAverage(point, keys)).filter(Number.isFinite);
  return finiteValuesForKey(flightData, prefix);
}

export function reportAtAGlanceTurboCardModel(summary, {
  panelNumber = defaultPanelNumber,
} = {}) {
  const metric = summary?.turbo;
  const details = metric?.details ?? {};
  if (!details.available || !reportTurboLoggedChannels(details).length) return null;
  const status = reportTurboContextStatus(metric);
  const card = {
    label: "Turbo Context",
    value: "Logged",
    detail: status,
    score: metric.score,
    color: Number.isFinite(metric.score) ? pdfColorForScore(metric.score) : pdfPalette.amber,
  };
  if (Number.isFinite(details.peakTit)) {
    return {
      ...card,
      value: panelNumber(details.peakTit, " F"),
      detail: `Peak TIT - ${status}`,
      color: pdfPalette.red,
    };
  }
  if (Number.isFinite(details.peakMap)) {
    return {
      ...card,
      value: panelNumber(details.peakMap, " inHg", 1),
      detail: `Peak MAP - ${status}`,
      color: pdfPalette.purple,
    };
  }
  if (Number.isFinite(details.peakUpperDeck)) {
    return {
      ...card,
      value: panelNumber(details.peakUpperDeck, " psi", 1),
      detail: `Upper deck - ${status}`,
      color: pdfPalette.amber,
    };
  }
  if (Number.isFinite(details.peakCompressor)) {
    return {
      ...card,
      value: panelNumber(details.peakCompressor, " F"),
      detail: `Peak CDT - ${status}`,
      color: pdfPalette.red,
    };
  }
  if (Number.isFinite(details.peakInduction)) {
    return {
      ...card,
      value: panelNumber(details.peakInduction, " F"),
      detail: `Induction temp - ${status}`,
      color: pdfPalette.amber,
    };
  }
  return card;
}

export function reportAtAGlanceOilSystemCardModel(summary, {
  panelNumber = defaultPanelNumber,
} = {}) {
  const metric = summary?.oilSystem;
  if (!Number.isFinite(metric?.score)) return null;
  const summaries = metric.details?.summaries ?? [];
  if (!summaries.length) return null;
  const top = metric.details?.top ?? summaries[0] ?? {};
  const issue = top.issue;
  const pressure = top.kind === "pressure";
  const unit = pressure ? " psi" : " F";
  const pressureSummary = summaries.find((item) => item.kind === "pressure");
  const tempSummary = summaries.find((item) => item.kind === "temperature");
  const value = issue
    ? pressure && Number.isFinite(top.min)
      ? panelNumber(top.min, unit)
      : panelNumber(top.max, unit)
    : pressureSummary && Number.isFinite(pressureSummary.min)
      ? panelNumber(pressureSummary.min, " psi")
      : panelNumber(tempSummary?.max, " F");
  const detail = issue?.label
    ?? [
      pressureSummary ? `P min ${panelNumber(pressureSummary.min, " psi")}` : "",
      tempSummary ? `T max ${panelNumber(tempSummary.max, " F")}` : "",
    ].filter(Boolean).join(" - ")
    ?? metric.note;
  return {
    label: "Oil System",
    value,
    detail: detail || metric.note,
    score: metric.score,
    color: Number.isFinite(metric.score) ? pdfColorForScore(metric.score) : pdfPalette.amber,
  };
}

export function reportAtAGlanceCardsModel(summary, status, primaryFinding, primaryMetric, {
  flightData = [],
  panelNumber = defaultPanelNumber,
  quantile = defaultQuantile,
  preferredPowerEstimateSummary = () => null,
} = {}) {
  const powerSummary = preferredPowerEstimateSummary(flightData);
  const chtValues = reportTemperatureValuesModel("cht", { flightData });
  const coolantValues = finiteValuesForKey(flightData, "coolantTemp");
  const egtValues = reportTemperatureValuesModel("egt", { flightData });
  const chtMax = summary.cht?.details?.max ?? extentFromValues(chtValues)?.max ?? null;
  const chtP95 = summary.cht?.details?.p95 ?? quantile(chtValues, 0.95);
  const coolantMax = summary.coolantMargin?.details?.max ?? extentFromValues(coolantValues)?.max ?? null;
  const coolantP95 = summary.coolantMargin?.details?.p95 ?? quantile(coolantValues, 0.95);
  const egtSpreadP90 = summary.egt?.details?.p90;
  const egtSpreadMax = summary.egt?.details?.max;
  const hotEgt = summary.egt?.details?.hot;
  const egtP90 = quantile(egtValues, 0.9);
  const egtMax = extentFromValues(egtValues)?.max ?? null;
  const statusDetail = String(primaryFinding?.title ?? "Automated advisory screen")
    .replace(/^Probe data needs review on\s+/i, "")
    .replace(/\s+and\s+/g, " + ");
  const turboCard = reportAtAGlanceTurboCardModel(summary, { panelNumber });
  const oilCard = reportAtAGlanceOilSystemCardModel(summary, { panelNumber });
  const rotaxProfileCard = reportRotaxProfileCard(summary);
  const heatCard = isRotaxSummary(summary) && (Number.isFinite(coolantMax) || Number.isFinite(coolantP95))
    ? {
        label: "Coolant Temp",
        value: panelNumber(coolantMax, " F"),
        detail: Number.isFinite(coolantP95) ? `95% ${panelNumber(coolantP95, " F")}` : summary.coolantMargin?.note ?? "Rotax cooling context",
        score: summary.coolantMargin?.score,
      }
    : {
        label: "Peak CHT",
        value: panelNumber(chtMax, " F"),
        detail: `95% ${panelNumber(chtP95, " F")}`,
        score: summary.cht?.score,
      };
  return [
    {
      label: "Overall Health",
      value: `${summary.overall.score}/100`,
      detail: summary.overall.label,
      score: summary.overall.score,
    },
    {
      label: "Engine Status",
      value: status.label,
      detail: primaryFinding?.title ? `${statusDetail} probe review` : statusDetail,
      score: primaryMetric?.score ?? summary.overall.score,
    },
    ...(rotaxProfileCard ? [rotaxProfileCard] : []),
    heatCard,
    {
      label: hotEgt?.isReview && (hotEgt.elevatedChannels?.length ?? 0) > 1 ? "High EGTs" : hotEgt?.isReview ? "Peak EGT" : Number.isFinite(egtSpreadP90) ? "EGT Spread" : "EGT Trace",
      value: panelNumber(hotEgt?.isReview ? hotEgt.max : egtSpreadP90 ?? egtP90, " F"),
      detail: hotEgt?.isReview
        ? (hotEgt.elevatedChannels?.length ?? 0) > 1
          ? `${hotEgt.elevatedChannels.length} channels high; peak ${hotEgt.label ?? "EGT"}`
          : `${hotEgt.label ?? "EGT"} high absolute`
        : Number.isFinite(egtSpreadMax) ? `max ${panelNumber(egtSpreadMax, " F")}` : `peak ${panelNumber(egtMax, " F")}`,
      score: summary.egt?.score,
    },
    ...(oilCard ? [oilCard] : []),
    ...(turboCard ? [turboCard] : []),
    {
      label: powerSummary?.cardLabel ?? "Avg Power",
      value: Number.isFinite(powerSummary?.avgHp) ? `${Math.round(powerSummary.avgHp)} hp` : "Not logged",
      detail: Number.isFinite(powerSummary?.avgTorque) ? `${Math.round(powerSummary.avgTorque)} lb-ft torque` : powerSummary?.basis ?? "Power estimate unavailable",
      color: pdfPalette.blue,
    },
  ];
}

export function reportCylinderTableRowsModel(cylinderRows) {
  return cylinderRows.map((row) => {
    const flags = row.flags.length ? row.flags : reportCylinderScoreDriverLabels(row);
    return [
      row.label ?? `Cyl ${row.number}`,
      `${row.metric.score}/100`,
      row.chtMax,
      row.chtP95,
      row.chtPeer,
      row.egtP90,
      flags.length ? flags.join(", ") : "No focused flags",
    ];
  });
}

function reportCylinderScoreDriverLabels(row) {
  const metric = row?.metric ?? {};
  if (!Number.isFinite(metric.score) || metric.score >= 88) return [];
  const candidates = [
    ["Probe confidence", metric.probeScore],
    ["CHT exposure", metric.chtScore],
    ["EGT peer spread", metric.egtScore],
    [metric.valveFlagged ? "EGT rhythm" : null, metric.valveScore],
    [metric.inductionLeakFlagged ? "Induction leak" : null, metric.inductionLeakScore],
    [metric.coolingRateFlagged ? "Cooling rate" : null, metric.coolingRateScore],
    ["CHT/EGT pairing", metric.pairingScore],
    ["Peer comparison", metric.peerScore],
  ]
    .filter(([label, score]) => label && Number.isFinite(score) && score < 88)
    .sort((a, b) => a[1] - b[1])
    .map(([label]) => label);
  return [...new Set(candidates)].slice(0, 2);
}
